import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { COUNTRY_CODES } from '@/config/countryCodes';
import { ArrowDropDownIcon, SearchIcon } from './AuthIcons';
import styles from './CountryCodeSelect.module.css';

/**
 * CountryCodeSelect — the "+91 ▾ |" prefix of the Mobile No field and its country list.
 *
 * The prefix mirrors Flutter WebSignUpFormNew's prefixIcon (web_signup_page.dart). The list opens
 * as a dropdown under the field, with a search box and "+code  Name" rows, filtered by name or code
 * as the user types — where Flutter opens CustomDialog.showCountryCodeBottomSheet, a mobile-style
 * bottom sheet (doc/FLUTTER_DEVIATIONS.md, "Country code picker"). Arrow keys move through the
 * list, Enter picks, Escape or a click outside closes it.
 *
 * The dropdown is positioned against the field's input box (AuthTextField), so it spans the field.
 */

interface CountryCodeSelectProps {
  value: string;
  onChange: (phoneCode: string) => void;
  disabled?: boolean;
}

export function CountryCodeSelect({ value, onChange, disabled }: CountryCodeSelectProps) {
  const { t } = useLocalization();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** The first scroll after opening centres the selected country; later ones follow the arrow keys */
  const scrollBlock = useRef<'center' | 'nearest'>('center');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return COUNTRY_CODES;
    const matches = COUNTRY_CODES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.phoneCode.startsWith(q) || c.iso2.toLowerCase() === q,
    );
    // Names starting with the query first ("mala" → Malaysia before Guatemala)
    const rank = (name: string) => (name.toLowerCase().startsWith(q) ? 0 : 1);
    return matches.sort((a, b) => rank(a.name) - rank(b.name));
  }, [query]);

  // Keep the active row visible
  useEffect(() => {
    if (!open) return;
    // Scrolls the list only — scrollIntoView would scroll the page too
    const list = listRef.current;
    const row = list?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    if (!list || !row) return;
    const listBox = list.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();
    if (scrollBlock.current === 'center') {
      list.scrollTop += rowBox.top - listBox.top - (list.clientHeight - rowBox.height) / 2;
      scrollBlock.current = 'nearest';
    } else if (rowBox.top < listBox.top) {
      list.scrollTop += rowBox.top - listBox.top;
    } else if (rowBox.bottom > listBox.bottom) {
      list.scrollTop += rowBox.bottom - listBox.bottom;
    }
  }, [open, activeIndex]);

  // A click outside closes it
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  /** Opens on the selected country, centred in the list */
  function openMenu() {
    scrollBlock.current = 'center';
    setActiveIndex(Math.max(COUNTRY_CODES.findIndex((c) => c.phoneCode === value), 0));
    setOpen(true);
  }

  function close(returnFocus = false) {
    setOpen(false);
    setQuery('');
    if (returnFocus) triggerRef.current?.focus();
  }

  function select(phoneCode: string) {
    onChange(phoneCode);
    close(true);
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const country = filtered[activeIndex] ?? filtered[0];
      if (country) select(country.phoneCode);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    } else if (e.key === 'Tab') {
      close();
    }
  }

  const activeCountry = filtered[activeIndex];

  return (
    <span className={styles.root} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => (open ? close() : openMenu())}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${t('Country code')} +${value}`}
      >
        <span>{`+${value} `}</span>
        <ArrowDropDownIcon size={20} className={open ? styles.caretOpen : undefined} />
        <span className={styles.divider} aria-hidden />
      </button>

      {open && (
        <div className={styles.menu}>
          <div className={styles.searchBox}>
            <SearchIcon size={20} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t('Start typing to search')}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              autoFocus
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={activeCountry ? `${listId}-${activeCountry.iso2}` : undefined}
              aria-label={t('Search')}
            />
          </div>
          <ul id={listId} ref={listRef} className={styles.list} role="listbox" aria-label={t('Country code')}>
            {filtered.map((c, i) => (
              <li
                key={c.iso2}
                id={`${listId}-${c.iso2}`}
                data-index={i}
                role="option"
                aria-selected={c.phoneCode === value}
                className={[
                  styles.option,
                  i === activeIndex ? styles.optionActive : '',
                  c.phoneCode === value ? styles.optionSelected : '',
                ].join(' ')}
                onMouseEnter={() => setActiveIndex(i)}
                // mousedown keeps focus in the search box until the choice is made
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(c.phoneCode)}
              >
                <span className={styles.optionCode}>+{c.phoneCode}</span>
                <span className={styles.optionName}>{c.name}</span>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className={styles.empty} role="presentation">{t('No results')}</li>
            )}
          </ul>
        </div>
      )}
    </span>
  );
}
